import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { MailOpen } from "lucide-react";
import { MailListItem } from "./MailListItem";
import { MailDetail } from "./MailDetail";
import { GroupedEmails } from "./GroupedEmails";

const useDesktop = () => {
  const q = "(min-width: 1024px)";
  const [desk, setDesk] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const h = () => setDesk(m.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  return desk;
};

export function MailSplitView({ items, groups, header, footer, onChanged }) {
  const desktop = useDesktop();
  const [selId, setSelId] = useState(null);
  const lastIdx = useRef(0);
  const flat = groups ? groups.flatMap((g) => g.items) : items;
  const selected = flat.find((e) => e.id === selId) || null;

  useEffect(() => {
    if (selId && flat.some((e) => e.id === selId)) return;
    // Nach Erledigen/Zuordnen automatisch die nächste Mail zeigen
    if (desktop || selId) setSelId(flat.length ? flat[Math.min(lastIdx.current, flat.length - 1)].id : null);
  }, [flat, selId, desktop]);

  const select = (e) => {
    lastIdx.current = flat.findIndex((x) => x.id === e.id);
    setSelId(e.id);
    requestAnimationFrame(() => document.querySelector(`[data-testid="mail-row-${e.id}"]`)?.scrollIntoView({ block: "nearest" }));
  };
  const onKey = (ev) => {
    if (!["ArrowDown", "ArrowUp"].includes(ev.key) || !flat.length) return;
    ev.preventDefault();
    const i = Math.max(0, Math.min(flat.length - 1, flat.findIndex((x) => x.id === selId) + (ev.key === "ArrowDown" ? 1 : -1)));
    select(flat[i]);
  };
  const item = (e) => <MailListItem key={e.id} email={e} active={e.id === selId} onSelect={select} />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-[calc(100vh-15rem)] lg:min-h-[560px]" data-testid="mail-split-view">
      <section className="lg:col-span-5 xl:col-span-4 flex flex-col rounded-xl border bg-card shadow-sm overflow-hidden min-h-0">
        <div className="p-3 border-b bg-slate-50/60">{header}</div>
        <div className="flex-1 overflow-y-auto divide-y outline-none" tabIndex={0} onKeyDown={onKey} data-testid="mail-list-pane">
          {groups ? <GroupedEmails groups={groups} renderItem={item} /> : items.map(item)}
          {footer}
        </div>
      </section>
      {desktop ? (
        <section className="lg:col-span-7 xl:col-span-8 rounded-xl border bg-card shadow-sm overflow-hidden min-h-0" data-testid="mail-reading-pane">
          {selected ? <MailDetail email={selected} onChanged={onChanged} /> : (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground text-sm"><MailOpen className="w-8 h-8" />Mail links auswählen</div>
          )}
        </section>
      ) : (
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelId(null)}>
          <SheetContent side="bottom" className="h-[92vh] p-0 rounded-t-2xl" onOpenAutoFocus={(ev) => ev.preventDefault()} data-testid="mail-reading-sheet">
            <SheetTitle className="sr-only">E-Mail</SheetTitle>
            {selected && <MailDetail email={selected} onChanged={onChanged} />}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
